import { LOCALE_ID, CUSTOM_ELEMENTS_SCHEMA, NgModule } from '@angular/core';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NgxSpinnerModule } from 'ngx-spinner';
import { BrowserModule, Meta } from '@angular/platform-browser';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { ClipboardModule } from '@angular/cdk/clipboard';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { RouterModule } from '@angular/router';
import { ErrorHandlerService } from '@services/error-handler/error-handler.service';
import { AuthInterceptor } from './http-interceptors/auth.interceptor';
import { AppComponent } from './app.component';
import { NavMenuComponent } from '@components/navigation/nav-menu/nav-menu.component';
import { HomeComponent } from '@components/pages/home/home.component';
import { RegisterUserComponent } from '@components/authentication/register-user/register-user.component';
import { LoginComponent } from '@components/authentication/login/login.component';
import { JwtModule } from "@auth0/angular-jwt";
import { AuthenticationModule } from '@components/authentication/authentication.module';
import { SelectEntitiesService } from '@services/entity/select-entities.service';
import { CommonModule, DatePipe, DecimalPipe } from '@angular/common';
import { UsersTableComponent } from '@components/entities/users/users-table/users-table.component';
import { ForbiddenComponent } from '@components/pages/forbidden/forbidden.component';
import { ForgotPasswordComponent } from '@components/authentication/forgot-password/forgot-password.component';
import { ResetPasswordComponent } from '@components/authentication/reset-password/reset-password.component';
import { EmailConfirmationComponent } from '@components/authentication/email-confirmation/email-confirmation.component';
import { IfRolesDirective } from './directives/if-roles.directive';
import { EditUserComponent } from '@components/entities/users/edit-user/edit-user.component';
import { DashboardComponent } from './components/dashboard/dashboard.component';
import { LogoutCallbackComponent } from './components/authentication/logout-callback/logout-callback.component';
import { LayoutModule } from '@angular/cdk/layout';
import { TableComponent } from '@components/abstract/table/table.component';
import { CreateUserComponent } from './components/entities/users/create-user/create-user.component';
import { CreateUserDialogComponent } from './components/dialog/create-user-dialog/create-user-dialog.component';
import { CreateUserDialogContentComponent } from './components/dialog/create-user-dialog/create-user-dialog-content/create-user-dialog-content.component';
import { DeleteUserDialogComponent } from './components/dialog/delete-user-dialog/delete-user-dialog.component';
import { DeleteUserDialogContentComponent } from './components/dialog/delete-user-dialog/delete-user-dialog-content/delete-user-dialog-content.component';
import { NgbdSortableHeader } from './directives/sortable.directive';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { ChargingStationsTableComponent } from './components/entities/charging-stations/charging-stations-table/charging-stations-table.component';
import { ChargingStationsComponent } from './components/entities/charging-stations/charging-stations.component';
import { ChargingStationsModule } from '@components/entities/charging-stations/charging-stations.module';
import { BlackboxesModule } from '@components/entities/blackboxes/blackboxes.module';
import { CreateChargingStationComponent } from './components/entities/charging-stations/create-charging-station/create-charging-station.component';
import { CreateChargingStationDialogComponent } from '@components/dialog/create-charging-station-dialog/create-charging-station-dialog.component';
import { CreateChargingStationDialogContentComponent } from '@components/dialog/create-charging-station-dialog/create-charging-station-dialog-content/create-charging-station-dialog-content.component';
import { CreateChargingStationAssistentComponent } from '@components/entities/charging-stations/create-charging-station-assistent/create-charging-station-assistent.component';
import { CreateChargingStationAssistentDialogComponent } from '@components/dialog/create-charging-station-assistent-dialog/create-charging-station-assistent-dialog.component';
import { CreateChargingStationAssistentDialogContentComponent } from '@components/dialog/create-charging-station-assistent-dialog/create-charging-station-assistent-dialog-content/create-charging-station-assistent-dialog-content.component';
import { DetailsChargingStationComponent } from './components/entities/charging-stations/details-charging-station/details-charging-station.component';
import { DetailsChargingStationDialogComponent } from './components/dialog/details-charging-station-dialog/details-charging-station-dialog.component';
import { DetailsChargingStationDialogContentComponent } from './components/dialog/details-charging-station-dialog/details-charging-station-dialog-content/details-charging-station-dialog-content.component';
import { ForgotPasswordSuccessComponent } from './components/authentication/forgot-password-success/forgot-password-success.component';
import { DashboardRunningTransactionsComponent } from '@components/dashboard/dashboard-running-transactions/dashboard-running-transactions.component';
import { registerLocaleData } from '@angular/common';
import localeDe from '@angular/common/locales/de';
import localeDeExtra from '@angular/common/locales/extra/de';
import { UpgradeComponent } from './components/pages/upgrade/upgrade.component';
import { ManageComponent } from './components/manage/manage.component';
import { ManageModule } from './components/manage/manage.module';
import { RfidCardsTableComponent } from './components/entities/rfid-cards/rfid-cards-table/rfid-cards-table.component';
import { UnassignedRfidCardsTableComponent } from './components/entities/rfid-cards/unassigned-rfid-cards-table/unassigned-rfid-cards-table.component';
import { RemoveRfidAssignmentDialogContentComponent } from './components/dialog/remove-rfid-assignment-dialog-content/remove-rfid-assignment-dialog-content.component';
import { NgSelectModule } from '@ng-select/ng-select';
import { EnableFreeChargeDialogContentComponent } from './components/dialog/enable-free-charge-dialog-content/enable-free-charge-dialog-content.component';
import { EnablePublicChargeDialogContentComponent } from './components/dialog/enable-public-charge-dialog-content/enable-public-charge-dialog-content.component';
import { EnablePrivateChargeDialogContentComponent } from './components/dialog/enable-private-charge-dialog-content/enable-private-charge-dialog-content.component';
import { LeafletModule } from '@asymmetrik/ngx-leaflet';
import { RemoteStartStopDialogContentComponent } from './components/dialog/remote-start-stop-dialog-content/remote-start-stop-dialog-content.component';
import { EditUserDialogComponent } from './components/dialog/edit-user-dialog/edit-user-dialog.component';
import { EditUserDialogContentComponent } from './components/dialog/edit-user-dialog/edit-user-dialog-content/edit-user-dialog-content.component';
import { AuthGuard } from '@guards/auth/auth.guard';
import { ChargingstationTransactionsTableComponent } from './components/entities/transactions/chargingstation-transactions-table/chargingstation-transactions-table.component';
import { ThousandPipe } from './pipes/thousand.pipe';
import { KwhPipe } from './pipes/kwh.pipe';
import { SubstractDatePipe } from './pipes/datediff.pipe';
import { ProfileComponent } from './components/manage/profile/profile.component';
import { DeleteChargingStationDialogComponent } from './components/dialog/delete-charging-station-dialog/delete-charging-station-dialog.component';
import { DeleteChargingStationDialogContentComponent } from './components/dialog/delete-charging-station-dialog/delete-charging-station-dialog-content/delete-charging-station-dialog-content.component';
import { DashboardChargingPointsComponent } from './components/dashboard/dashboard-charging-points/dashboard-charging-points.component';
import { CustomerGuard } from './guards/customer/customer.guard';
import { ChargingStationRfidAuthComponent } from './components/entities/charging-stations/charging-station-rfid-auth/charging-station-rfid-auth.component';
import { DlmChargingStationComponent } from './components/entities/charging-stations/charging-station-dlm/dlm-charging-station.component';
import { SysChargingStationComponent } from './components/entities/charging-stations/charging-station-system/system-charging-station.component';
import { DashboardPowerChartComponent } from './components/dashboard/dashboard-power-chart/dashboard-power-chart.component';
import { DashboardProfileComponent } from './components/dashboard/dashboard-profile/dashboard-profile.component';
import { DashboardTransactionsComponent } from './components/dashboard/dashboard-transactions/dashboard-transactions.component';
import { GroupsComponent } from './components/manage/groups/groups.component';
import { DEDatePipe } from './pipes/de-date.pipe';
import { DEDatePipeShort } from './pipes/de-date-short.pipe';
import { ChangePasswordDialogContentComponent } from './components/dialog/change-password-dialog-content/change-password-dialog-content.component';
import { DeleteUserProfileDialogContentComponent } from './components/dialog/delete-user-profile-dialog-content/delete-user-profile-dialog-content.component';
import { EvaluationComponent } from './components/evaluation/evaluation.component';
import { EvaluationModule } from './components/evaluation/evaluation.module';
import { DeliveryNotesComponent } from './components/evaluation/delivery-notes/delivery-notes.component';
import { CreateGroupsComponent } from './components/manage/groups/create-groups/create-groups.component';
import { EditGroupsComponent } from './components/manage/groups/edit-groups/edit-groups.component';
import { ChargingStatisticsComponent } from './components/evaluation/charging-statistics/charging-statistics.component';
import { TransactionsComponent } from './components/evaluation/transactions/transactions.component';
import { AGBComponent } from './components/authentication/agb/agb.component';
import { DatasecurityComponent } from './components/authentication/datasecurity/datasecurity.component';
import { EmailChangeConfirmationComponent } from './components/authentication/email-change-confirmation/email-change-confirmation.component';
import { CustomersTableComponent } from './components/entities/customers/customers-table/customers-table.component';
import { CustomersModule } from './components/entities/customers/customers.module';
import { MessageComponent } from './components/abstract/message/message.component';
import { OperatorGuard } from '@guards/operator/operator.guard';
import { RoleGuard } from '@guards/role/role.guard';
import { CustomerOverviewComponent } from './components/entities/customers/customer-overview/customer-overview.component';
import { RegisterUserSuccessComponent } from './components/authentication/register-user-success/register-user-success.component';
import { RegisterUserSuccessWithInviteComponent } from './components/authentication/register-user-success-with-invite/register-user-success-with-invite.component';
import { CreateRfidCardComponent } from './components/entities/rfid-cards/create-rfid-card/create-rfid-card.component';
import { CreateRfidCardDialogComponent } from './components/dialog/create-rfid-card-dialog/create-rfid-card-dialog.component';
import { CreateRfidCardDialogContentComponent } from './components/dialog/create-rfid-card-dialog/create-rfid-card-dialog-content/create-rfid-card-dialog-content.component';
import { InvoicesModule } from './components/entities/invoices/invoices.module';
import { PricingsModule } from './components/entities/pricings/pricings.module';
import { PricingsComponent } from './components/entities/pricings/pricings.component';
import { PricingsTableComponent } from './components/entities/pricings/pricings-table/pricings-table.component';
import { CreatePricingComponent } from './components/entities/pricings/create-pricing/create-pricing.component';
import { CreatePublicPricingComponent } from './components/entities/pricings/create-public-pricing/create-public-pricing.component';
import { CreatePricingDialogContentComponent } from './components/dialog/create-pricing-dialog/create-pricing-dialog-content/create-pricing-dialog-content.component';
import { CreatePublicPricingDialogContentComponent } from './components/dialog/create-public-pricing-dialog/create-public-pricing-dialog-content/create-public-pricing-dialog-content.component';
import { EditPricingDialogContentComponent } from './components/dialog/edit-pricing-dialog/edit-pricing-dialog-content/edit-pricing-dialog-content.component';
import { DeletePricingDialogContentComponent } from './components/dialog/delete-pricing-dialog/delete-pricing-dialog-content/delete-pricing-dialog-content.component';
import { UsedTokenFeedbackComponent } from './components/authentication/used-token-feedback/used-token-feedback.component';
import { WeekPipe } from './pipes/week.pipe';
import { ChangePasswordDialogForCustomerContentComponent } from './components/dialog/change-password-for-customer-dialog-content/change-password-for-customer-dialog-content.component';
import { DeleteUserForCustomerDialogContentComponent } from './components/dialog/delete-user-for-customer-dialog-content/delete-user-for-customer-dialog-content.component';
import { DeleteRFIDCardForCustomerDialogContentComponent } from './components/dialog/delete-rfid-card-for-customer-dialog-content/delete-rfid-card-for-customer-dialog-content.component';
import { CustomerHistoryComponent } from './components/entities/customers/customer-history/customer-history.component';
import { RestartChargingStationDialogContentComponent } from '@components/dialog/restart-charging-station-dialog-content/restart-charging-station-dialog-content.component';
import { UsersModule } from '@components/entities/users/users.module';
import { ResetChargingStationPasswordDialogContentComponent } from './components/dialog/reset-charging-station-password-dialog-content/reset-charging-station-password-dialog-content.component';
import { ChangeCustomerComponent } from './components/navigation/change-customer/change-customer.component';
import { CustomerPermissionsComponent } from './components/entities/customers/customer-permissions/customer-permissions.component';
import { OperatorComponent } from './components/operator/operator.component';
import { SubscriptionTypesComponent } from './components/operator/subscription-types/subscription-types.component';
import { OperatorModule } from '@components/operator/operator.module';
import { ChangePasswordSuccessContentComponent } from './components/dialog/change-password-success-dialog-content/change-password-success-dialog-content.component';
import { RfidScanDialogContentComponent } from './components/dialog/rfid-scan-dialog-content/rfid-scan-dialog-content.component';
import { DeliveryNoteTemplateComponent } from './components/pdf/delivery-note-template/delivery-note-template.component';
import { SupportComponent } from './components/pages/support/support.component';
import { AngularIbanModule } from 'angular-iban';
import { DeleteGroupDialogComponent } from './components/dialog/delete-group-dialog/delete-group-dialog.component';
import { FooterComponent } from './components/navigation/footer/footer.component';
import { AboutUsDialogComponent } from './components/navigation/about-us/about-us-dialog.component';
import { CreateDeliveryNoteDialogContentComponent } from './components/dialog/create-delivery-note-dialog-content/create-delivery-note-dialog-content.component';
import { CreateDeliveryCSVDialogContentComponent } from './components/dialog/create-delivery-csv-dialog-content/create-delivery-csv-dialog-content.component';
import { CreateDeliveryNoteComponent } from './components/entities/delivery-notes/create-delivery-note/create-delivery-note.component';
import { CreateDeliveryCSVComponent } from './components/entities/delivery-notes/create-delivery-csv/create-delivery-csv.component';
import { ToastsComponent } from '@components/abstract/toast/toast.component';
import { RestartChargingStationWarningDialogContentComponent } from './components/dialog/restart-charging-station-warning-dialog-content/restart-charging-station-warning-dialog-content.component';
import { ResetPasswordWarningDialogContentComponent } from './components/dialog/reset-charging-station-password-warning-dialog-content/reset-charging-station-password-warning-dialog-content.component';
import { PaymentSuccessComponent } from './components/payments/payment-success/payment-success.component';
import { PaymentCanceledComponent } from './components/payments/payment-canceled/payment-canceled.component';
import { ChargingStationsStatusHistoryTableComponent } from './components/entities/charging-stations/charging-stations-status-history-table/charging-stations-status-history-table.component';
import { CancelSubscriptionDialogContentComponent } from './components/dialog/cancel-subscription-dialog-content/cancel-subscription-dialog-content.component';
import { EditGroupDialogComponent } from './components/dialog/edit-group-dialog/edit-group-dialog.component';
import { EditRfidCardDialogComponent } from './components/dialog/edit-rfid-card-dialog/edit-rfid-card-dialog.component';
import { EditRfidCardDialogContentComponent } from '@components/dialog/edit-rfid-card-dialog/edit-rfid-card-dialog-content/edit-rfid-card-dialog-content.component';
import { DeliveryNoteActionsDialogComponent } from './components/dialog/delivery-note-actions-dialog/delivery-note-actions-dialog.component';
import { DeliveryNoteActionsDialogContentComponent } from './components/dialog/delivery-note-actions-dialog/delivery-note-actions-dialog-content/delivery-note-actions-dialog-content.component';
import { EditDeliveryNotesSettingsDialogComponent } from './components/dialog/edit-delivery-notes-settings-dialog/edit-delivery-notes-settings-dialog.component';
import { EditDeliveryNotesSettingsDialogContentComponent } from './components/dialog/edit-delivery-notes-settings-dialog/edit-delivery-notes-settings-dialog-content/edit-delivery-notes-settings-dialog-content.component';
import { PublicChargePlansComponent } from '@components/public-charge/public-charge-plans/public-charge-plans.component';
import { PublicChargeComponent } from '@components/public-charge/public-charge/public-charge.component';
import { PublicChargeCanceledComponent } from '@components/public-charge/public-charge-canceled/public-charge-canceled.component';
import { CreateCustomerComponent } from './components/entities/customers/create-customer/create-customer.component';
import { CreateCustomerDialogComponent } from './components/dialog/create-customer-dialog/create-customer-dialog.component';
import { CreateCustomerDialogContentComponent } from './components/dialog/create-customer-dialog/create-customer-dialog-content/create-customer-dialog-content.component';
import { DetailsTransactionDialogComponent } from './components/dialog/details-transaction-dialog/details-transaction-dialog.component';
import { DetailsTransactionDialogContentComponent } from './components/dialog/details-transaction-dialog/details-transaction-dialog-content/details-transaction-dialog-content.component';
import { DetailsRunningTransactionDialogComponent } from './components/dialog/details-running-transaction-dialog/details-running-transaction-dialog.component';
import { DetailsRunningTransactionDialogContentComponent } from './components/dialog/details-running-transaction-dialog/details-running-transaction-dialog-content/details-running-transaction-dialog-content.component';
import { DetailsTransactionComponent } from './components/entities/transactions/details-transaction/details-transaction.component';
import { DeleteDeliveryNoteDialogContentComponent } from './components/dialog/delete-delivery-note-dialog-content/delete-delivery-note-dialog-content';
import { UpdateChargingStationsDialogContentComponent } from './components/dialog/update-charging-station-dialog/update-charging-station-dialog-content/update-charging-stations-dialog-content';
import { SVGComponent } from '@components/abstract/svg/svg.component';
import { UpvotyComponent } from '@components/abstract/upvoty/upvoty.component';
import { ImageCropperModule } from 'ngx-image-cropper';
import { ChargingParkSubscriptionDialogContentComponent } from './components/dialog/charging-park-subscription-dialog/charging-park-subscription-dialog-content/charging-park-subscription-dialog-content.component';
import { ChargingParkSubscriptionDialogComponent } from './components/dialog/charging-park-subscription-dialog/charging-park-subscription-dialog.component';
import { ChargingParkSubscriptionComponent } from './components/manage/profile/charging-park-subscription/charging-park-subscription.component';
import { InvoicesComponent } from '@components/entities/invoices/invoices.component';
import { InvoicesTableComponent } from '@components/entities/invoices/invoices-table/invoices-table.component';
import { OpenInvoicesTableComponent } from '@components/entities/invoices/open-invoices-table/open-invoices-table.component';
import { CreateInvoiceDialogContentComponent } from '@components/dialog/create-invoice-dialog/create-invoice-dialog-content/create-invoice-dialog-content.component';
import { PrintInvoiceDialogContentComponent } from '@components/dialog/print-invoice-dialog/print-invoice-dialog-content/print-invoice-dialog-content.component';
import { InvoicePDFComponent } from '@components/pdf/invoice-pdf/invoice-pdf.component';
import { EditInvoiceSettingsDialogComponent } from '@components/dialog/edit-invoice-settings-dialog/edit-invoice-settings-dialog.component';
import { EditInvoiceSettingsDialogContentComponent } from '@components/dialog/edit-invoice-settings-dialog/edit-invoice-settings-dialog-content/edit-invoice-settings-dialog-content.component';
import { ManufacturerTableComponent } from '@components/operator/manufacturer/manufacturer.component';
import { ChargingStationProfilesComponent } from '@components/operator/chargingstationprofiles/chargingstationprofiles.component';
import { CreateChargingStationProfileComponent } from '@components/entities/charging-stations-profile/create-chargingstation-profile/create-chargingstationprofile.component'
import { CreateChargingStationProfileDialogComponent } from '@components/dialog/create-chargingstationprofile-dialog/create-chargingstationprofile-dialog.component'
import { CreateChargingStationProfileDialogContentComponent } from '@components/dialog/create-chargingstationprofile-dialog/create-chargingstationprofile-dialog-content/create-chargingstationprofile-dialog-content.component'
import { StatusNotificationsTableComponent } from '@components/operator/statusnotifications/statusnotifications.component';
import { CreateManufacturerComponent } from '@components/entities/manufacturer/create-manufacturer/create-manufacturer.component';
import { CreateManufacturerDialogComponent } from '@components/dialog/create-manufacturer-dialog/create-manufacturer-dialog.component'
import { CreateManufacturerDialogContentComponent } from './components/dialog/create-manufacturer-dialog/create-manufacturer-dialog-content/create-manufacturer-dialog-content.component';
import { DiagnosticChargingStationDialogComponent } from './components/dialog/diagnostic-charging-station-dialog/diagnostics-charging-station-dialog-component'
import { DiagnosticChargingStationDialogContentComponent } from './components/dialog/diagnostic-charging-station-dialog/diagnostic-charging-station-dialog-content/diagnostics-charging-station-dialog-content.component'
import { UsersComponent } from '@components/entities/users/users.component';
import { UserOverviewComponent } from '@components/entities/users/user-overview/user-overview.component';
import { QRCodeModule } from 'angularx-qrcode';
import { NgxMaskModule, IConfig } from 'ngx-mask';
import { PaymentReceiptsTableComponent } from '@components/entities/payment-receipts/payment-receipts-table/payment-receipts-table.component';
import { PaymentReceiptsComponent } from '@components/entities/payment-receipts/payment-receipts.component';
import { PaymentReceiptsModule } from '@components/entities/payment-receipts/payment-receipts.module';
import { InviteCustomerDialogComponent } from '@components/dialog/invite-customer-dialog/invite-customer-dialog.component';
import { InviteCustomerDialogContentComponent } from '@components/dialog/invite-customer-dialog/invite-customer-dialog-content/invite-customer-dialog-content.component';
import { WelcomeUserComponent } from '@components/authentication/welcome-user/welcome-user.component';
import { TableFilterComponent } from '@components/abstract/table/filter/table-filter.component';
import { DashboardOfflineSinceComponent } from '@components/operator/dashboard-offlineSince/dashboard-offlineSince.component';
import { BlackboxesTableComponent } from '@components/entities/blackboxes/blackboxes-table/blackboxes-table.component';
import { BlackboxesComponent } from '@components/entities/blackboxes/blackboxes.component';
import { EnergyMonitorModule } from '@components/energy/energy-monitor/evaluation.module';
import { EnergyMonitorComponent } from '@components/energy/energy-monitor/energy-monitor.component';
import { EnergyDistributionChartComponent } from '@components/charts/energy-distribution-chart.component';
import { EnergyProductionChartComponent } from '@components/charts/energy-production-chart.component';
import { EnergyConsumptionChartComponent } from '@components/charts/energy-consumption-chart.component';
import { EnergyHistoryChartComponent } from '@components/charts/energy-history-chart.component';
import { EnergyFlowChartComponent } from '@components/charts/energy-flow-chart.component';
import { BlackboxSetupComponent } from '@components/entities/blackboxes/blackbox-setup/blackbox-setup.component';
import { SetupBlackboxDialogComponent } from '@components/dialog/setup-blackbox-dialog/setup-blackbox-dialog.component';
import { SetupBlackboxDialogContentComponent } from '@components/dialog/setup-blackbox-dialog/setup-blackbox-dialog-content/setup-blackbox-dialog-content.component';
import { AutofocusDirective } from './directives/autofocus.directive';
import { ClaimAutochargeIdComponent } from '@components/authentication/claim-autochargeId/claim-autochargeId.component';
import { RegisterUserFromOperatorComponent } from '@components/operator/register-user-operator/register-user-operator.component'
import { ChargingPowerProfileBadge } from '@components/abstract/badges/charging-power-profile-badge/charging-power-profile-badge.component';
import { TransactionStatusBadge } from '@components/abstract/badges/transaction-status-badge/transaction-status-badge.component';
import { RunningTransactionPopupComponent } from '@components/dashboard/dashboard-running-transactions/running-transaction-popup/running-transaction-popup.component';
import { RequestSupportPopupComponent } from '@components/dialog/request-support/request-support-popup.component';
import { PaymentMethodAddedDialogContentComponent } from '@components/dialog/payment-method-added-dialog-content/payment-method-added-dialog-content.component';
import { PayoutStatementsComponent } from '@components/entities/payout-statements/payout-statements.component';
import { PayoutPreviewComponent } from '@components/entities/invoices/payout-preview/payout-preview.component';
import { PayoutStatementsDialogComponent } from '@components/dialog/payout-statements-dialog/payout-statements-dialog.component';
import { PayoutStatementsDialogContentComponent } from '@components/dialog/payout-statements-dialog/payout-statements-dialog-content/payout-statements-dialog-content.component';
import { CreditStatementsDialogComponent } from '@components/dialog/credit-statements-dialog/credit-statements-dialog.component';
import { CreditStatementsDialogContentComponent } from '@components/dialog/credit-statements-dialog/credit-statements-dialog-content/credit-statements-dialog-content.component';
import { CreditStatementsComponent } from '@components/entities/credit-statements/credit-statements.component';
import { EditEmailDialogComponent } from './components/dialog/edit-email-dialog/edit-email-dialog.component';
import { EditEmailDialogContentComponent } from './components/dialog/edit-email-dialog/edit-email-dialog-content/edit-email-dialog-content.component';
import { RemovePricingDialogContentComponent } from '@components/dialog/remove-pricing-dialog/remove-pricing-dialog-content/remove-pricing-dialog-content.component';
import { ChargingStationInfo } from '@components/entities/charging-stations/charging-station-info/charging-station-info.component';
import { ChargingStationCombinedStatusBadge } from '@components/abstract/badges/charging-station-combined-status-badge/charging-station-combined-status-badge.component';
import { RfidModule } from './components/entities/rfid-cards/rfid.module';
import { EnergyModule } from './components/energy/energy.module';
import { RfidComponent } from './components/entities/rfid-cards/rfid.component';
import { BidiChargingComponent } from './components/energy/bidi-charging/bidi-charging.component';
import { DualRangeSliderComponent } from './components/energy/bidi-charging/dual-range-slider/dual-range-slider.component';
import { RangeSliderComponent } from './components/energy/bidi-charging/range-slider/range-slider.component';
import { NgxScannerQrcodeModule } from 'ngx-scanner-qrcode';
import { BidiSelectChargingStationComponent } from './components/energy/bidi-charging/bidi-select-charging-station/bidi-select-charging-station.component';
import { EnergyTabNavigationComponent } from '@components/energy/energy-monitor/energy-tab-navigation/energy-tab-navigation.component';


import { EnergyComponent } from './components/energy/energy.component';


export const options: Partial<null | IConfig> | (() => Partial<IConfig>) = null;

export function tokenGetter() {
  return localStorage.getItem("token");
}

interface NgxSpinnerConfig {
  type?: string;
}

registerLocaleData(localeDe, 'de', localeDeExtra);

@NgModule({
  declarations: [
    AppComponent,
    TableComponent,
    NavMenuComponent,
    HomeComponent,
    RegisterUserComponent,
    LoginComponent,
    WelcomeUserComponent,
    UsersTableComponent,
    EditUserComponent,
    ForgotPasswordComponent,
    ForgotPasswordSuccessComponent,
    ResetPasswordComponent,
    EmailConfirmationComponent,
    IfRolesDirective,
    DashboardComponent,
    LogoutCallbackComponent,
    CreateUserComponent,
    CreateUserDialogComponent,
    CreateUserDialogContentComponent,
    CreateCustomerComponent,
    CreateCustomerDialogComponent,
    CreateCustomerDialogContentComponent,
    DeleteUserDialogComponent,
    DeleteUserDialogContentComponent,
    NgbdSortableHeader,
    ChargingStationsTableComponent,
    ChargingStationsComponent,
    CreateChargingStationComponent,
    CreateChargingStationDialogComponent,
    CreateChargingStationDialogContentComponent,
    CreateChargingStationAssistentComponent,
    CreateChargingStationAssistentDialogComponent,
    CreateChargingStationAssistentDialogContentComponent,
    DetailsChargingStationComponent,
    DetailsChargingStationDialogComponent,
    DetailsChargingStationDialogContentComponent,
    UpgradeComponent,
    ManageComponent,
    RfidCardsTableComponent,
    UnassignedRfidCardsTableComponent,
    RemoveRfidAssignmentDialogContentComponent,
    EditRfidCardDialogContentComponent,
    ClaimAutochargeIdComponent,
    EnableFreeChargeDialogContentComponent,
    EnablePublicChargeDialogContentComponent,
    EnablePrivateChargeDialogContentComponent,
    DiagnosticChargingStationDialogComponent,
    DiagnosticChargingStationDialogContentComponent,
    RemoteStartStopDialogContentComponent,
    EditUserDialogComponent,
    EditUserDialogContentComponent,
    ChargingstationTransactionsTableComponent,
    ThousandPipe,
    KwhPipe,
    DeleteChargingStationDialogComponent,
    DeleteChargingStationDialogContentComponent,
    ChargingStationRfidAuthComponent,
    DlmChargingStationComponent,
    SysChargingStationComponent,
    ProfileComponent,
    InviteCustomerDialogComponent,
    InviteCustomerDialogContentComponent,
    DashboardChargingPointsComponent,
    DashboardPowerChartComponent,
    DashboardProfileComponent,
    DashboardTransactionsComponent,
    GroupsComponent,
    DEDatePipe,
    DEDatePipeShort,
    WeekPipe,
    ChangePasswordDialogContentComponent,
    DeleteUserProfileDialogContentComponent,
    EvaluationComponent,
    DeliveryNotesComponent,
    CreateGroupsComponent,
    EditGroupsComponent,
    ChargingStatisticsComponent,
    TransactionsComponent,
    EmailChangeConfirmationComponent,
    AGBComponent,
    DatasecurityComponent,
    CustomersTableComponent,
    MessageComponent,
    CustomerOverviewComponent,
    RegisterUserSuccessComponent,
    RegisterUserSuccessWithInviteComponent,
    CreateRfidCardComponent,
    CreateRfidCardDialogComponent,
    CreateRfidCardDialogContentComponent,

    InvoicesComponent,
    InvoicesTableComponent,
    OpenInvoicesTableComponent,
    CreateInvoiceDialogContentComponent,
    PrintInvoiceDialogContentComponent,
    InvoicePDFComponent,
    EditInvoiceSettingsDialogComponent,
    EditInvoiceSettingsDialogContentComponent,
    PaymentReceiptsComponent,
    PaymentReceiptsTableComponent,

    PricingsComponent,
    PricingsTableComponent,
    CreatePricingComponent,
    CreatePricingDialogContentComponent,
    CreatePublicPricingComponent,
    CreatePublicPricingDialogContentComponent,
    EditPricingDialogContentComponent,
    DeletePricingDialogContentComponent,
    RemovePricingDialogContentComponent,

    UsedTokenFeedbackComponent,
    ChangePasswordDialogForCustomerContentComponent,
    DeleteUserForCustomerDialogContentComponent,
    DeleteRFIDCardForCustomerDialogContentComponent,
    CustomerHistoryComponent,
    RestartChargingStationDialogContentComponent,
    ResetChargingStationPasswordDialogContentComponent,
    ChangeCustomerComponent,
    CustomerPermissionsComponent,
    OperatorComponent,
    SubscriptionTypesComponent,
    ChangePasswordSuccessContentComponent,
    RfidScanDialogContentComponent,
    DeliveryNoteTemplateComponent,
    SupportComponent,
    DeleteGroupDialogComponent,
    FooterComponent,
    AboutUsDialogComponent,
    CreateDeliveryNoteDialogContentComponent,
    CreateDeliveryNoteComponent,
    CreateDeliveryCSVDialogContentComponent,
    CreateDeliveryCSVComponent,
    ToastsComponent,
    RestartChargingStationWarningDialogContentComponent,
    ResetPasswordWarningDialogContentComponent,
    PaymentSuccessComponent,
    PaymentCanceledComponent,
    ChargingStationsStatusHistoryTableComponent,
    CancelSubscriptionDialogContentComponent,
    EditGroupDialogComponent,
    EditRfidCardDialogComponent,
    DeliveryNoteActionsDialogComponent,
    DeliveryNoteActionsDialogContentComponent,
    PublicChargePlansComponent,
    PublicChargeComponent,
    PublicChargeCanceledComponent,
    DetailsTransactionDialogComponent,
    DetailsTransactionDialogContentComponent,
    DetailsTransactionComponent,
    DeleteDeliveryNoteDialogContentComponent,
    SVGComponent,
    UpvotyComponent,
    DashboardRunningTransactionsComponent,
    RunningTransactionPopupComponent,
    RequestSupportPopupComponent,
    DetailsRunningTransactionDialogComponent,
    DetailsRunningTransactionDialogContentComponent,
    StatusNotificationsTableComponent,
    ManufacturerTableComponent,
    CreateManufacturerComponent,
    CreateManufacturerDialogComponent,
    CreateManufacturerDialogContentComponent,

    ChargingStationProfilesComponent,
    CreateChargingStationProfileComponent,
    CreateChargingStationProfileDialogComponent,
    CreateChargingStationProfileDialogContentComponent,

    UpdateChargingStationsDialogContentComponent,
    ChargingParkSubscriptionDialogContentComponent,
    ChargingParkSubscriptionDialogComponent,
    ChargingParkSubscriptionComponent,
    EditDeliveryNotesSettingsDialogComponent,
    EditDeliveryNotesSettingsDialogContentComponent,
    TableFilterComponent,
    DashboardOfflineSinceComponent,
    BlackboxesComponent,
    BlackboxesTableComponent,
    EnergyMonitorComponent,
    EnergyTabNavigationComponent,
    EnergyDistributionChartComponent,
    EnergyProductionChartComponent,
    EnergyConsumptionChartComponent,
    EnergyHistoryChartComponent,
    EnergyFlowChartComponent,
    BlackboxSetupComponent,
    SetupBlackboxDialogComponent,
    SetupBlackboxDialogContentComponent,
    ChargingPowerProfileBadge,
    TransactionStatusBadge,
    ChargingStationCombinedStatusBadge,
    PaymentMethodAddedDialogContentComponent,
    PayoutStatementsComponent,
    PayoutPreviewComponent,
    PayoutStatementsDialogComponent,
    PayoutStatementsDialogContentComponent,
    CreditStatementsComponent,
    CreditStatementsDialogComponent,
    CreditStatementsDialogContentComponent,
    UsersComponent,
    UserOverviewComponent,
    RegisterUserFromOperatorComponent,
    AutofocusDirective,
    EditEmailDialogComponent,
    EditEmailDialogContentComponent,
    ChargingStationInfo,
    RfidComponent,
    BidiChargingComponent,
    DualRangeSliderComponent,
    RangeSliderComponent,
    BidiSelectChargingStationComponent,
    EnergyComponent
  ],
  imports: [
    BrowserModule.withServerTransition({ appId: 'ng-cli-universal' }),
    BrowserAnimationsModule,
    NgxSpinnerModule.forRoot({ type: 'ball-scale-multiple' }),
    ClipboardModule,
    CommonModule,
    HttpClientModule,
    FormsModule,
    ReactiveFormsModule,
    NgbModule,
    ScrollingModule,
    NgSelectModule,
    AuthenticationModule,
    ChargingStationsModule,
    BlackboxesModule,
    ManageModule,
    InvoicesModule,
    PaymentReceiptsModule,
    PricingsModule,
    CustomersModule,
    UsersModule,
    OperatorModule,
    EvaluationModule,
    AngularIbanModule,
    QRCodeModule,
    EnergyModule,
    RfidModule,
    RouterModule.forRoot([
      {
        path: '', component: DashboardComponent,
        pathMatch: 'full',
        canActivate: [AuthGuard, RoleGuard],
        data: {
          roles: ['User', 'Customer', 'Operator']
        }
      },
      { path: 'bidi-charging', component: BidiChargingComponent },
      { path: 'energy', component: EnergyComponent },
      { path: 'pricings', component: PricingsComponent },
      { path: 'invoices', component: InvoicesComponent },
      { path: 'support', component: SupportComponent },
      {
        path: 'profil',
        component: ProfileComponent,
        children: [
          {
            path: 'email',
            component: EditEmailDialogComponent
          }
        ]
      },
      { path: 'payment-success', component: PaymentSuccessComponent },
      { path: 'payment-canceled', component: PaymentCanceledComponent },
      { path: 'public-charge-plans', component: PublicChargePlansComponent },
      { path: 'pc', component: PublicChargePlansComponent },
      { path: 'public-charge', component: PublicChargeComponent },
      { path: 'public-charge-canceled', component: PublicChargeCanceledComponent },
      { path: 'logout-callback', component: LogoutCallbackComponent },
      { path: 'forbidden', component: ForbiddenComponent },
      { path: 'upgrade', component: UpgradeComponent },
      { path: 'pdf', component: DeliveryNoteTemplateComponent },
      { path: '**', redirectTo: '' },

    ], { relativeLinkResolution: 'legacy', anchorScrolling: 'enabled' }),
    JwtModule.forRoot({
      config: {
        tokenGetter: tokenGetter,
        allowedDomains: ["localhost:44371"],
        disallowedRoutes: []
      }
    }),
    LayoutModule,
    LeafletModule,
    ImageCropperModule,
    EnergyMonitorModule,
    NgxMaskModule.forRoot(),
    NgxScannerQrcodeModule
  ],
  providers: [
    { provide: LOCALE_ID, useValue: 'de-DE' },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorHandlerService,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },
    Meta,
    DecimalPipe,
    ThousandPipe,
    KwhPipe,
    DatePipe,
    DEDatePipe,
    WeekPipe,
    SubstractDatePipe,
    SelectEntitiesService
  ],
  bootstrap: [AppComponent, UpvotyComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class AppModule { }
